import React, { useEffect, useState } from "react";
import { useSeo } from "../useSeo";
import { SERMONS, SERMONS_PLAYLIST_URL, PODCAST_EPISODES, PODCAST_CHANNEL_URL, SCHOOL_VIDEOS, SCHOOL_PLAYLIST_URL } from "../data";
import ChannelsList from "../ChannelsList";
import { apiGet } from "../../api/client";

// Ultimos videos/episodios buscados da API nesta sessao da SPA (modulo so
// e avaliado uma vez) -- mesma ideia do cache em Eventos.jsx: sem isso, toda
// vez que a pessoa navega de outra pagina pra /pregacoes o componente
// remonta do zero com o fallback fixo (SERMONS/PODCAST_EPISODES) e so troca
// pelos videos reais quando a API responde de novo, dando um "pisca" visivel
// (titulos/miniaturas trocando) mesmo quando a lista ja tinha sido buscada
// minutos antes.
let cachedSermons = null;
let cachedPodcastEpisodes = null;
let cachedSchoolVideos = null;

function youTubeIdFromUrl(url) {
  const m = url.match(/[?&]v=([^&]+)/);
  return m ? m[1] : null;
}

function ListCard({ num, title, url }) {
  const videoId = youTubeIdFromUrl(url);
  return (
    <a className={`card list-card${videoId ? " has-thumb" : ""}`} href={url} target="_blank" rel="noopener">
      <span className="thumb-wrap">
        {videoId && (
          <img
            className="thumb"
            src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
            alt={title}
            loading="lazy"
            width="240"
            height="135"
          />
        )}
        {num != null && <span className="num">{num}</span>}
      </span>
      <div className="body"><h3>{title}</h3></div>
      <span className="when" style={{ display: "flex", alignItems: "center", gap: 4 }}>
        Assistir
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M7 17 17 7M9 7h8v8" /></svg>
      </span>
    </a>
  );
}

// Card compacto do destaque do topo (1 por categoria) -- visual proprio
// (vertical, thumb grande em cima) pra nao parecer "mais uma linha" igual
// aos ListCard horizontais das listas abaixo (pedido do Rodrigo, 10/2026:
// um resumo de 1-de-cada logo no inicio, sem precisar abrir cada secao).
// Reaproveita o .highlight-card que ja existia no CSS institucional sem
// nenhuma pagina usando -- so faltava este caso de uso.
function FeaturedCard({ tag, title, url }) {
  const videoId = youTubeIdFromUrl(url);
  return (
    <a className="card highlight-card" href={url} target="_blank" rel="noopener">
      <div className="highlight-media">
        <span className="highlight-thumb-wrap">
          {videoId && (
            <img
              src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
              alt={title}
              loading="lazy"
              width="320"
              height="180"
            />
          )}
        </span>
        <span className="highlight-tag">{tag}</span>
      </div>
      <div className="highlight-body">
        <h3>{title}</h3>
      </div>
    </a>
  );
}

export default function Pregacoes() {
  useSeo({
    title: "Pregações e Podcast Fé e Café | Igreja Batista Mosaico",
    description: "Assista às pregações recentes e ouça o podcast Fé e Café do Pr. Rodrigo Moraes, direto no canal oficial do YouTube da Igreja Batista Mosaico.",
    path: "/pregacoes"
  });

  // Começa com as listas fixas (nunca fica vazio) e troca pelos vídeos mais
  // recentes do canal quando a API responde -- se a busca falhar ou vier
  // vazia, mantém o fallback fixo (ver mediaRoutes no backend).
  const [sermons, setSermons] = useState(() => cachedSermons || SERMONS);
  const [podcastEpisodes, setPodcastEpisodes] = useState(() => cachedPodcastEpisodes || PODCAST_EPISODES);
  const [schoolVideos, setSchoolVideos] = useState(() => cachedSchoolVideos || SCHOOL_VIDEOS.slice(0, 5));

  useEffect(() => {
    apiGet("/media/sermons", { skipSessionExpiredHandler: true })
      .then((data) => {
        const items = data?.items;
        if (Array.isArray(items) && items.length > 0) {
          const mapped = items.slice(0, 5).map((item) => ({ title: item.title, url: item.url }));
          cachedSermons = mapped;
          setSermons(mapped);
        }
      })
      .catch(() => {
        /* mantém o fallback fixo em SERMONS */
      });

    apiGet("/media/podcast", { skipSessionExpiredHandler: true })
      .then((data) => {
        const items = data?.items;
        if (Array.isArray(items) && items.length > 0) {
          // O feed do YouTube não traz o número real do episódio -- usa
          // posição (01, 02...) em vez de inventar um número.
          const mapped = items.slice(0, 5).map((item, i) => ({ ep: String(i + 1).padStart(2, "0"), title: item.title, url: item.url }));
          cachedPodcastEpisodes = mapped;
          setPodcastEpisodes(mapped);
        }
      })
      .catch(() => {
        /* mantém o fallback fixo em PODCAST_EPISODES */
      });

    apiGet("/media/school-videos", { skipSessionExpiredHandler: true })
      .then((data) => {
        const items = data?.items;
        if (Array.isArray(items) && items.length > 0) {
          const mapped = items.slice(0, 5).map((item) => ({ title: item.title, url: item.url }));
          cachedSchoolVideos = mapped;
          setSchoolVideos(mapped);
        }
      })
      .catch(() => {
        /* mantém o fallback fixo em SCHOOL_VIDEOS */
      });
  }, []);

  return (
    <>
      <div className="section-head" style={{ marginTop: 34 }}>
        <p className="eyebrow">Pregações &amp; mídia</p>
        <h1>Vídeos: pregações, podcast e Escola Bíblica</h1>
        <p>Vídeos e episódios reais do canal do Pr. Rodrigo Moraes.</p>
      </div>

      {/* Destaque: 1 video de cada categoria logo no topo, pra quem chega
          na pagina ver de cara que saiu coisa nova em TODAS as frentes --
          sem isso, só dava pra saber clicando em cada âncora abaixo uma
          por uma (pedido do Rodrigo, 10/2026). As listas completas logo
          abaixo continuam mostrando esses mesmos itens de novo (primeiro
          de cada lista) -- repetição esperada aqui, mesmo padrão de
          "Destaques" + lista completa do Spotify/Netflix, não é bug. */}
      {(sermons[0] || podcastEpisodes[0] || schoolVideos[0]) && (
        <div className="grid grid-3" style={{ marginTop: 22 }}>
          {sermons[0] && <FeaturedCard tag="Pregação" title={sermons[0].title} url={sermons[0].url} />}
          {podcastEpisodes[0] && <FeaturedCard tag="Podcast" title={podcastEpisodes[0].title} url={podcastEpisodes[0].url} />}
          {schoolVideos[0] && <FeaturedCard tag="Escola Bíblica" title={schoolVideos[0].title} url={schoolVideos[0].url} />}
        </div>
      )}

      {/* Sub-nav de âncoras -- com 3 blocos empilhados (pregações, podcast,
          escola bíblica com 10 vídeos) e nada indicando o que existe mais
          embaixo, quem entra atrás de um específico tinha que rolar por
          tudo. Isso dá um "sumário" logo no topo pra pular direto, sem
          esconder nada do DOM (mantém SEO/crawlers vendo tudo). */}
      <nav className="section-jump-nav" aria-label="Ir para seção">
        <a href="#sec-pregacoes">Pregações</a>
        <a href="#sec-podcast">Podcast</a>
        <a href="#sec-escola-biblica">Escola Bíblica</a>
      </nav>

      <div className="section-head" id="sec-pregacoes" style={{ marginTop: 0 }}><h2 style={{ fontSize: 16 }}>Pregações recentes</h2></div>
      <div className="list-stack">
        {sermons.map((s, i) => <ListCard key={s.url} num={String(i + 1).padStart(2, "0")} title={s.title} url={s.url} />)}
      </div>
      <a className="see-more-link" href={SERMONS_PLAYLIST_URL} target="_blank" rel="noopener">Ver mais pregações no canal do YouTube
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M7 17 17 7M9 7h8v8" /></svg>
      </a>

      <div className="section-head" id="sec-podcast" style={{ marginTop: 28 }}>
        <h2 style={{ fontSize: 16 }}>Podcast Fé e Café</h2>
        <p style={{ marginTop: 6 }}>Episódios do canal do Pr. Rodrigo Moraes no YouTube.</p>
      </div>
      <div className="list-stack">
        {podcastEpisodes.map((s) => <ListCard key={s.url} title={s.title} url={s.url} />)}
      </div>
      <a className="see-more-link" href={PODCAST_CHANNEL_URL} target="_blank" rel="noopener">Ver mais episódios no canal do YouTube
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M7 17 17 7M9 7h8v8" /></svg>
      </a>

      <div className="section-head" id="sec-escola-biblica" style={{ marginTop: 28 }}>
        <h2 style={{ fontSize: 16 }}>Escola Bíblica</h2>
        <p style={{ marginTop: 6 }}>Aulas da Escola Bíblica Mosaico, na ordem da playlist do canal.</p>
      </div>
      <div className="list-stack">
        {schoolVideos.map((s) => <ListCard key={s.url} title={s.title} url={s.url} />)}
      </div>
      <a className="see-more-link" href={SCHOOL_PLAYLIST_URL} target="_blank" rel="noopener">Ver a playlist completa no YouTube
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M7 17 17 7M9 7h8v8" /></svg>
      </a>

      <ChannelsList title="Mais canais" />
    </>
  );
}
